import React, { type ReactElement, useEffect, useState } from 'react'
import { Box, CircularProgress, LinearProgress, Tooltip } from '@mui/material'

import { typography } from '~tokens/internal'

import Icon from '../Icon'
import IconButton from '../IconButton'
import Typography from '../Typography'

import './UploadProgress.scss'

export type UploadProgressItemStatus = 'uploading' | 'complete' | 'error'

export interface UploadProgressItem {
  /** Unique identifier for the item */
  id: string
  /** Display name of the item (e.g. file name) */
  name: string
  /** Current status of the item */
  status: UploadProgressItemStatus
  /** Number of units processed so far */
  processedCount?: number | null
  /** Total number of units to process */
  totalCount?: number | null
  /** Label for the units being processed (e.g. "records") */
  unitLabel?: string
  /** Message shown under the name when the item is complete */
  completeMessage?: string
  /** Message shown under the name when the item failed */
  errorMessage?: string
}

export interface UploadProgressProps {
  /** Panel header title (e.g. "Uploading 1 item") */
  title: string
  /** Items to display in the panel; the panel is hidden when empty */
  items: UploadProgressItem[]
  /** Callback when the close button is clicked */
  onClose?: () => void
}

const formatCount = (count: number): string => count.toLocaleString()

/**
 * Floating upload progress panel (Google Drive style), anchored to the
 * bottom-right corner of the viewport.
 */
const UploadProgress: React.FC<UploadProgressProps> = ({ title, items, onClose }) => {
  const [collapsed, setCollapsed] = useState(false)
  const [sawUploading, setSawUploading] = useState(false)

  const uploadingCount = items.filter((item) => item.status === 'uploading').length

  useEffect(() => {
    if (uploadingCount > 0) setSawUploading(true)
    if (items.length === 0) setSawUploading(false)
  }, [uploadingCount, items.length])

  if (items.length === 0) return null

  const allFinished = uploadingCount === 0

  const renderStatusLabel = (item: UploadProgressItem): ReactElement => {
    if (item.status === 'error') {
      return (
        <Typography variant='body1' className='upload-progress__status--error' noWrap>
          {item.errorMessage ?? 'Upload failed'}
        </Typography>
      )
    }
    return (
      <Typography variant='body1' className='upload-progress__status' noWrap>
        {item.status === 'complete'
          ? 'Upload complete'
          : (item.processedCount ?? 0) > 0
            ? 'Uploading...'
            : 'Starting upload...'}
      </Typography>
    )
  }

  const renderItemSubtitle = (item: UploadProgressItem): string | null => {
    if (item.status === 'error') return null
    if (item.totalCount != null && item.totalCount > 0) {
      const processed = item.status === 'complete' ? item.totalCount : (item.processedCount ?? 0)
      return `${formatCount(processed)} of ${formatCount(item.totalCount)} ${item.unitLabel ?? 'items'}`
    }
    if (item.status === 'complete') return item.completeMessage ?? null
    return null
  }

  const renderItemStatusIcon = (item: UploadProgressItem): ReactElement => {
    if (item.status === 'uploading') {
      return (
        <CircularProgress
          size={typography.fontSize.lg}
          thickness={4}
          className='upload-progress__spinner'
        />
      )
    }
    if (item.status === 'error') {
      return <Icon name='error' size={typography.fontSize.lg} />
    }
    return <Icon name='taskAlt' size={typography.fontSize.lg} />
  }

  const renderItem = (item: UploadProgressItem): ReactElement => {
    const subtitle = renderItemSubtitle(item)
    const progressPercent =
      item.status === 'complete'
        ? 100
        : item.status === 'uploading' && item.totalCount != null && item.totalCount > 0
          ? Math.min(100, Math.floor(((item.processedCount ?? 0) / item.totalCount) * 100))
          : null
    return (
      <Box key={item.id} className='upload-progress__item'>
        {renderStatusLabel(item)}
        <Box className='upload-progress__row'>
          <Box className='upload-progress__file-icon'>
            <Icon name='description' size={typography.fontSize.xl} />
          </Box>
          <Box className='upload-progress__file-info'>
            <Typography variant='body1' noWrap>
              {item.name}
            </Typography>
            {subtitle !== null && (
              <Typography variant='body2' className='upload-progress__subtitle' noWrap>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            className={`upload-progress__status-icon${
              item.status === 'error'
                ? ' upload-progress__status-icon--error'
                : item.status === 'complete'
                  ? ' upload-progress__status-icon--complete'
                  : ''
            }`}
          >
            {renderItemStatusIcon(item)}
          </Box>
        </Box>
        {item.status !== 'error' && (
          <Box className='upload-progress__bar-wrap'>
            <LinearProgress
              variant={progressPercent !== null ? 'determinate' : 'indeterminate'}
              value={progressPercent ?? undefined}
              className='upload-progress__bar'
            />
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box
      className={`upload-progress${allFinished && sawUploading ? ' upload-progress--finished' : ''}`}
    >
      <Box
        className={`upload-progress__header${collapsed ? ' upload-progress__header--collapsed' : ''}`}
      >
        <Typography variant='subtitle1' className='upload-progress__title'>
          {title}
        </Typography>
        <Box className='upload-progress__actions'>
          <Tooltip title={collapsed ? 'Expand' : 'Collapse'}>
            <IconButton
              size='small'
              aria-label={collapsed ? 'Expand upload progress' : 'Collapse upload progress'}
              onClick={() => setCollapsed((prev) => !prev)}
            >
              <Icon name={collapsed ? 'expandLess' : 'expandMore'} fontSize='small' />
            </IconButton>
          </Tooltip>
          {onClose && (
            <Tooltip title='Close'>
              <IconButton size='small' aria-label='Close upload progress' onClick={onClose}>
                <Icon name='close' fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      {!collapsed && <Box className='upload-progress__items'>{items.map(renderItem)}</Box>}
    </Box>
  )
}

export default UploadProgress
